import { bool, string, number } from "prop-types";
import React from "react";
import { twMerge } from "tailwind-merge";

import { useTranslation, Trans } from "@lib/i18n";

import Typography from "@components/shared/Typography";

import { getFormattedPrice } from "@shared/v2/lib/getFormattedPrice";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { getDealCarteGrise } from "@shared/v2/lib/getDealCarteGrise";
import { useDealSelector } from "@redux/reducers/deal";
import { useCarteGriseConfigSelector } from "@redux/reducers/appConfig";
import { getIsVehicleRegistrationByCitroenSelected } from "@shared/v2/lib/getIsVehicleRegistrationByCitroenSelected";
import { getCarteGriseAmount } from "@shared/v2/lib/getCarteGriseAmount";

const FinancePriceDetail = ({
  isFinanceRequestError,
  amount,
  deposit,
  priceFormat,
  currencySymbol,
  withDeliveryInfo,
  withFinanceWidgetButton,
  isQuantityVisible,
  vehicleQuantity,
}) => {
  const { t } = useTranslation();
  const isB2BSelected = useIsB2BSelected();
  const deal = useDealSelector();
  const carteGriseConfig = useCarteGriseConfigSelector();

  const formattedAmount = getFormattedPrice(
    amount,
    priceFormat,
    currencySymbol
  );

  const dealCarteGrise = getDealCarteGrise(deal);

  const carteGriseAmount = getCarteGriseAmount(deal);

  const isRegistrationByCitroen = getIsVehicleRegistrationByCitroenSelected(
    deal.financeSimulation.vehicleRegistration
  );
  const isCarteGriseMentionVisible =
    carteGriseConfig?.enabled && isRegistrationByCitroen;

  return (
    <>
      {isFinanceRequestError ? (
        <Typography className="text-white text-lg text-center font-semibold m-auto max-w-80 px-4 text-balance">
          {t("financeDataErrorMessage")}
        </Typography>
      ) : (
        <div
          className={twMerge(
            "flex flex-col justify-center items-end h-full p-2 z-10 text-white",
            withFinanceWidgetButton &&
              "relative mr-2 sm:mr-6 after:content-[''] after:absolute after:top-1/5 after:bottom-1/5 after:-right-1 after:w-px after:bg-greyLight sm:after:-right-3",
            "sm:p-4 md:p-2 lg:px-0 lg:py-1"
          )}
          data-id="price-switcher-price-container"
        >
          <Typography
            className={
              "text-white text-2xl text-right font-semibold sm:text-3xl md:text-2xl lg:text-3xl"
            }
            data-id="price-switcher-formatted-price"
          >
            {isQuantityVisible && vehicleQuantity > 1 && (
              <span className="text-lg">
                {vehicleQuantity}{" "}
                <span className="text-xxs font-extrabold">x</span>{" "}
              </span>
            )}
            {formattedAmount}{" "}
            <sup className="text-xs pl-1 largeMobile:text-sm">
              {isB2BSelected ? t("price.b2b.month") : t("price.b2c.month")}
            </sup>
          </Typography>
          <div className="flex flex-col ml-auto text-right pt-1 text-xxs tracking-wider font-extrabold">
            {Boolean(deposit) && (
              <Typography
                className="text-white text-sm text-right font-semibold"
                data-id="price-switcher-deposit-text"
              >
                {isQuantityVisible && vehicleQuantity > 1 && (
                  <span className="text-xs">
                    {vehicleQuantity}{" "}
                    <span className="text-xxs font-extrabold">x</span>{" "}
                  </span>
                )}
                {t("price.afterFirstPayment", {
                  downPayment: getFormattedPrice(
                    Math.ceil(deposit),
                    priceFormat,
                    currencySymbol
                  ),
                })}
              </Typography>
            )}

            {isCarteGriseMentionVisible && (
              <Typography className="block text-white tracking-wider text-xs text-right font-extrabold whitespace-break-words">
                <span
                  className="whitespace-nowrap"
                  data-id="price-switcher-cartegrise-text"
                >
                  {t(
                    `price.carteGrise.finance.${
                      dealCarteGrise && isRegistrationByCitroen
                        ? "included"
                        : "excluded"
                    }`,
                    {
                      cost: getFormattedPrice(
                        carteGriseAmount,
                        priceFormat,
                        currencySymbol
                      ),
                    }
                  )}
                </span>
              </Typography>
            )}
            {withDeliveryInfo && (
              <Typography
                className="text-white text-xxs text-right tracking-wider font-extrabold"
                data-id="price-switcher-deliveryinfo-text"
              >
                <Trans>{t("price.deliveryInfo.priceDetail")}</Trans>
              </Typography>
            )}
          </div>
        </div>
      )}
    </>
  );
};

FinancePriceDetail.propTypes = {
  priceFormat: string.isRequired,
  isFinanceRequestError: bool,
  amount: number,
  deposit: number,
  currencySymbol: string.isRequired,
  withDeliveryInfo: bool,
  withFinanceWidgetButton: bool,
  isQuantityVisible: bool,
  vehicleQuantity: number,
};

export default FinancePriceDetail;
